import { CartSessionKeyProvider, useRegionCurrency, CART_QUERY_KEYS } from '@skin-seoul/service';
import { useQueryClient } from '@tanstack/react-query';
import {
  CartDrawerContextProvider,
  useCartDrawer,
  ShoppingBagDrawer as ShoppingBagDrawerComponent,
  ShoppingBagButton as ShoppingBagButtonComponent,
} from '@skin-seoul/shared-components';
import type { ComponentProps } from 'react';
import { useEffect, useRef } from 'react';
import { BridgeApi, wpBridge } from '@skin-seoul/utils';

type ShoppingBagButtonProps = Pick<ComponentProps<typeof ShoppingBagButtonComponent>, 'iconProps'>;

const ShoppingBagButton = ({ iconProps }: ShoppingBagButtonProps) => {
  const { onCartToggle } = useCartDrawer();
  const { country } = useRegionCurrency();

  return <ShoppingBagButtonComponent country={country} onClick={() => onCartToggle()} iconProps={iconProps} />;
};

const ShoppingBagDrawer = () => {
  const { currency } = useRegionCurrency();
  const { onCartClose } = useCartDrawer();
  const push = (path: string) => {
    window.location.href = path;
  };

  const handleContinueShopping = () => {
    onCartClose();
    push('/');
  };

  return <ShoppingBagDrawerComponent currency={currency} onClickContinueShopping={handleContinueShopping} />;
};

type ShoppingBagDrawerButtonProps = ShoppingBagButtonProps & {
  listenToWPEvents?: boolean;
};

const CartDrawerWPListener = () => {
  const { onCartOpen, isOpen } = useCartDrawer();
  const queryClient = useQueryClient();
  const isOpenRef = useRef(isOpen);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    const off = wpBridge.on('add-to-cart', () => {
      queryClient.invalidateQueries({ queryKey: CART_QUERY_KEYS.default() });

      const currentIsOpen = isOpenRef.current;

      if (currentIsOpen) {
        return;
      }

      if (BridgeApi.isWebview()) {
        BridgeApi.cartOpen();
        return;
      }

      onCartOpen();
    });

    return off;
  }, [onCartOpen, queryClient]);

  return null;
};

const CartDrawerButton = ({ iconProps, listenToWPEvents = false }: ShoppingBagDrawerButtonProps) => {
  return (
    <CartDrawerContextProvider>
      <CartSessionKeyProvider>
        {listenToWPEvents && <CartDrawerWPListener />}
        <ShoppingBagButton iconProps={iconProps} />
        <ShoppingBagDrawer />
      </CartSessionKeyProvider>
    </CartDrawerContextProvider>
  );
};

export const HeaderCartButton = () => {
  return <CartDrawerButton iconProps={{ color: 'generic.0' }} listenToWPEvents={true} />;
};

export const MobileNavCartButton = () => {
  return <CartDrawerButton iconProps={{ color: 'neutral.200', strokeWidth: 0.5 }} listenToWPEvents={false} />;
};
